import React, { useState } from "react";

/**
 * Discount Toggle component for selecting pricing options
 * Updated for ClickToFlyResult change object format
 * 
 * NOTE: For alternate airports, flexibleEnd determines which end (departure or arrival) is shown.
 */
const DiscountToggle = ({
  category,
  options,
  selectedValue,
  onSelect,
  showTimePicker,
  departureAirport,
  arrivalAirport,
}) => {
  const [selectedTime, setSelectedTime] = useState("12:00");

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.abs(amount));
  };

  const formatPercent = (decimal) => {
    return `${(Math.abs(decimal) * 100).toFixed(1)}%`;
  };

  const renderSavings = (option) => {
    // Use change object format: { percent, dollars }
    const change = option.change || { percent: 0, dollars: 0 };
    const percentValue = change.percent || 0;
    const dollarValue = change.dollars || 0;

    if (dollarValue === 0 && percentValue === 0) {
      return <span className="neutral">$0</span>;
    }

    // Positive percent = increase (costs more), negative = discount
    if (option.isIncrease || percentValue > 0) {
      return (
        <span className="increase">
          +{formatCurrency(dollarValue)} ({formatPercent(percentValue)})
        </span>
      );
    }

    return (
      <span className="discount">
        -{formatCurrency(dollarValue)} ({formatPercent(percentValue)})
      </span>
    );
  };

  return (
    <div className="discount-toggle">
      <label className="toggle-label">{category}</label>
      <div className="toggle-options">
        {options.map((option, index) => {
          const isSelected =
            selectedValue && selectedValue.value === option.value;

          return (
            <div
              key={index}
              className={`option ${isSelected ? "selected" : ""} ${
                option.isIncrease ? "increase-option" : ""
              } ${option.isDefault ? "default-option" : ""}`}
              onClick={() => onSelect(option)}
            >
              <div className="option-label">
                {option.label}
                {option.isCurrent && (
                  <span className="current-indicator">You</span>
                )}
              </div>
              <div className="option-savings">{renderSavings(option)}</div>

              {/* Show time picker for exact departure time - ALWAYS visible */}
              {showTimePicker && option.type === "exact" && (
                <div className="time-picker-container">
                  <label htmlFor={`time-${index}`}>Select Time:</label>
                  <input
                    type="time"
                    id={`time-${index}`}
                    min="09:00"
                    max="21:00"
                    value={selectedTime}
                    onChange={(e) => setSelectedTime(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}

              {/* Helper text for exact airport with proper layout */}
              {option.isExact && departureAirport && arrivalAirport && (
                <div className="exact-airport-info">
                  <div className="airport-line">
                    <strong>Departure:</strong> {departureAirport}
                  </div>
                  <div className="airport-line">
                    <strong>Arrival:</strong> {arrivalAirport}
                  </div>
                </div>
              )}

              {/* Show alternate airports based on flexibleEnd */}
              {option.alternateInfo && (
                option.flexibleEnd === "arrival"
                  ? option.alternateInfo.arrivalOptions?.length > 0 && (
                      <div className="alternate-airports-info">
                        <div className="alternates arrival-alternates">
                          <strong>Arrival Alternates:</strong>
                          <span>
                            {option.alternateInfo.arrivalOptions
                              .map(
                                (apt) =>
                                  `${apt.code}${
                                    apt.distance ? ` (${apt.distance}mi)` : ""
                                  }`
                              )
                              .join(", ")}
                          </span>
                        </div>
                      </div>
                    )
                  : option.alternateInfo.departureOptions?.length > 0 && (
                      <div className="alternate-airports-info">
                        <div className="alternates departure-alternates">
                          <strong>Departure Alternates:</strong>
                          <span>
                            {option.alternateInfo.departureOptions
                              .map(
                                (apt) =>
                                  `${apt.code}${
                                    apt.distance ? ` (${apt.distance}mi)` : ""
                                  }`
                              )
                              .join(", ")}
                          </span>
                        </div>
                      </div>
                    )
              )}

              {/* Show time window if available */}
              {option.timeWindow && (
                <div className="time-window-info">
                  {option.timeWindow.startLabel} - {option.timeWindow.endLabel}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DiscountToggle;
